import { useState, useMemo, useCallback } from 'react';
import { useAdfElementTree, AdfElementNode, flattenTree } from './useAdfStandard';
import { useAdfTemplates, getTemplateBySlug } from './useAdfTemplates';
import {
  buildAttributeString,
  getRawExampleValue,
  formatAttributesForXml,
  hasTradeInAlternate,
  hasCoBuyerAlternate,
  sortElementsByPriority,
  type AttributeConfig,
} from '@/lib/adf/xml-utils';

/**
 * Options for XML generation
 */
export interface XmlGenerationOptions {
  includeComments?: boolean;
  indentSize?: number;
  includeOptionalAttributes?: boolean;
  includeExampleValues?: boolean;
  useMergeFields?: boolean;
}

/**
 * Statistics about the current selection
 */
export interface SelectionStats {
  totalSelected: number;
  requiredCount: number;
  optionalCount: number;
  maxDepth: number;
}

/**
 * Instance-specific options for building XML elements
 */
interface BuildInstanceOptions {
  vehicleInterest?: 'buy' | 'trade-in';
  isCoBuyer?: boolean;
}

/**
 * Get all descendant IDs from an element node
 */
function getDescendantIds(element: AdfElementNode): string[] {
  const ids: string[] = [];
  
  const traverse = (node: AdfElementNode) => {
    node.children.forEach(child => {
      ids.push(child.id);
      traverse(child);
    });
  };
  
  traverse(element);
  return ids;
}

/**
 * Get ancestor chain from element up to root
 */
function getAncestorChain(
  elementId: string,
  elementMap: Map<string, AdfElementNode>
): string[] {
  const ancestors: string[] = [];
  let current = elementMap.get(elementId);
  
  while (current?.parent_element_id) {
    ancestors.push(current.parent_element_id);
    current = elementMap.get(current.parent_element_id);
  }
  
  return ancestors;
}

/**
 * Calculate max depth of selected elements
 */
function calculateMaxDepth(
  selectedIds: Set<string>,
  elementMap: Map<string, AdfElementNode>
): number {
  let maxDepth = 0;
  
  selectedIds.forEach(id => {
    const element = elementMap.get(id);
    if (element) {
      let depth = 0;
      let current: AdfElementNode | undefined = element;
      while (current?.parent_element_id) {
        depth++;
        current = elementMap.get(current.parent_element_id);
      }
      maxDepth = Math.max(maxDepth, depth);
    }
  });
  
  return maxDepth;
}

/**
 * Build XML for a single element and its selected children
 * Uses database-driven example values - NO hardcoded content
 */
function buildXmlElement(
  element: AdfElementNode,
  selectedIds: Set<string>,
  indent: number,
  options: XmlGenerationOptions,
  instanceOptions?: BuildInstanceOptions
): string {
  const indentStr = ' '.repeat(indent * (options.indentSize ?? 2));
  const lines: string[] = [];
  
  const useMergeFields = options.useMergeFields ?? false;
  const elementSlug = element.slug.replace(/-/g, '_');
  
  // Build attributes using shared utility (database-driven)
  let attrStr = buildAttributeString(
    element.attributes as Record<string, AttributeConfig> | null,
    {
      includeOptional: options.includeOptionalAttributes ?? true,
      useMergeFields,
      elementSlug: element.slug,
      vehicleInterest: instanceOptions?.vehicleInterest,
      isCoBuyer: instanceOptions?.isCoBuyer,
    }
  );
  
  const attrPart = formatAttributesForXml(attrStr);
  
  // Get selected children and SORT them using shared utility
  const selectedChildren = element.children.filter(child => selectedIds.has(child.id));
  const sortedChildren = sortElementsByPriority(selectedChildren);
  
  // Add comment if enabled
  if (options.includeComments && element.description) {
    lines.push(`${indentStr}<!-- ${element.description} -->`);
  }
  
  if (sortedChildren.length === 0) {
    // Leaf element
    if (useMergeFields) {
      // Generate merge field placeholder: {{element_slug}}
      lines.push(`${indentStr}<${element.tag_name}${attrPart}>{{${elementSlug}}}</${element.tag_name}>`);
    } else if (options.includeExampleValues) {
      // Use raw example value to preserve attributes from database (matches AdfXmlPreview)
      const useAlternate = instanceOptions?.isCoBuyer || instanceOptions?.vehicleInterest === 'trade-in';
      const rawExample = getRawExampleValue(element, { useAlternate });
      
      if (rawExample) {
        // Use complete example XML from database (includes attributes like <id source="..." sequence="...">)
        lines.push(`${indentStr}${rawExample}`);
      } else {
        // No example value in database - render empty element with computed attributes
        lines.push(`${indentStr}<${element.tag_name}${attrPart}></${element.tag_name}>`);
      }
    } else {
      lines.push(`${indentStr}<${element.tag_name}${attrPart} />`);
    }
  } else {
    // Element with children
    lines.push(`${indentStr}<${element.tag_name}${attrPart}>`);
    
    // Recursively build sorted children (pass instanceOptions through)
    sortedChildren.forEach(child => {
      lines.push(buildXmlElement(child, selectedIds, indent + 1, options, instanceOptions));
    });
    
    // Close tag
    lines.push(`${indentStr}</${element.tag_name}>`);
  }
  
  return lines.join('\n');
}

/**
 * Hook for managing ADF subset builder state
 * Handles element selection, parent/child relationships, and XML generation
 */
export function useAdfBuilder() {
  const { data: elementTree, isLoading: elementsLoading, error } = useAdfElementTree();
  const { data: templates, isLoading: templatesLoading } = useAdfTemplates();
  
  const [selectedElements, setSelectedElements] = useState<Set<string>>(new Set());
  const [tradeInEnabled, setTradeInEnabled] = useState(false);
  const [coBuyerEnabled, setCoBuyerEnabled] = useState(false);
  
  // Combined loading state
  const isLoading = elementsLoading || templatesLoading;
  
  // Build element map for O(1) lookups
  const elementMap = useMemo(() => {
    if (!elementTree) return new Map<string, AdfElementNode>();
    
    const map = new Map<string, AdfElementNode>();
    const allElements = flattenTree(elementTree);
    allElements.forEach(el => map.set(el.id, el));
    return map;
  }, [elementTree]);
  
  // Build slug map for quick lookups
  const slugMap = useMemo(() => {
    if (!elementTree) return new Map<string, AdfElementNode>();
    
    const map = new Map<string, AdfElementNode>();
    const allElements = flattenTree(elementTree);
    allElements.forEach(el => map.set(el.slug, el));
    return map;
  }, [elementTree]);
  
  // Check if an element is selected
  const isSelected = useCallback((id: string): boolean => {
    return selectedElements.has(id);
  }, [selectedElements]);
  
  // Check if any child of an element is selected
  const isAnyChildSelected = useCallback((id: string): boolean => {
    const element = elementMap.get(id);
    if (!element) return false;
    
    const descendants = getDescendantIds(element);
    return descendants.some(descId => selectedElements.has(descId));
  }, [elementMap, selectedElements]);
  
  // Toggle element selection
  // When selecting: auto-select required ancestors
  // When deselecting: remove all descendants
  const toggleElement = useCallback((id: string) => {
    setSelectedElements(prev => {
      const newSet = new Set(prev);
      
      if (newSet.has(id)) {
        // Deselecting: remove element and all descendants
        newSet.delete(id);
        const element = elementMap.get(id);
        if (element) {
          getDescendantIds(element).forEach(descId => newSet.delete(descId));
        }
      } else {
        // Selecting: add element and all required ancestors
        newSet.add(id);
        const ancestors = getAncestorChain(id, elementMap);
        ancestors.forEach(ancestorId => newSet.add(ancestorId));
      }
      
      return newSet;
    });
  }, [elementMap]);
  
  // Select element and ALL its descendants
  const selectBranch = useCallback((id: string) => {
    setSelectedElements(prev => {
      const newSet = new Set(prev);
      const element = elementMap.get(id);
      
      if (element) {
        // Add element itself
        newSet.add(id);
        
        // Add all descendants
        getDescendantIds(element).forEach(descId => newSet.add(descId));
        
        // Add required ancestors
        const ancestors = getAncestorChain(id, elementMap);
        ancestors.forEach(ancestorId => newSet.add(ancestorId));
      }
      
      return newSet;
    });
  }, [elementMap]);
  
  // Deselect element and ALL its descendants
  const deselectBranch = useCallback((id: string) => {
    setSelectedElements(prev => {
      const newSet = new Set(prev);
      const element = elementMap.get(id);
      
      if (element) {
        // Remove element itself
        newSet.delete(id);
        
        // Remove all descendants
        getDescendantIds(element).forEach(descId => newSet.delete(descId));
      }
      
      return newSet;
    });
  }, [elementMap]);
  
  // Get IDs of all required elements
  const getRequiredElements = useCallback((): string[] => {
    if (!elementTree) return [];
    
    const allElements = flattenTree(elementTree);
    return allElements
      .filter(el => el.is_required)
      .map(el => el.id);
  }, [elementTree]);
  
  // Auto-select all required elements
  const selectRequired = useCallback(() => {
    const requiredIds = getRequiredElements();
    setSelectedElements(prev => {
      const newSet = new Set(prev);
      requiredIds.forEach(id => {
        newSet.add(id);
        // Also add ancestors of required elements
        const ancestors = getAncestorChain(id, elementMap);
        ancestors.forEach(ancestorId => newSet.add(ancestorId));
      });
      return newSet;
    });
  }, [getRequiredElements, elementMap]);
  
  // Clear all selections and reset quick actions
  const clearSelection = useCallback(() => {
    setSelectedElements(new Set());
    setTradeInEnabled(false);
    setCoBuyerEnabled(false);
  }, []);

  // Apply a template preset from database (select all elements in template)
  const applyTemplate = useCallback((templateSlug: string) => {
    const template = getTemplateBySlug(templates, templateSlug);
    if (!template || !elementTree) return;
    
    const allElements = flattenTree(elementTree);
    
    // Set quick actions based on template settings
    setTradeInEnabled(template.enable_trade_in);
    setCoBuyerEnabled(template.enable_co_buyer);
    
    setSelectedElements(() => {
      const newSet = new Set<string>();
      
      // Add all template elements by slug
      template.element_slugs.forEach(slug => {
        const element = allElements.find(el => el.slug === slug);
        if (element) {
          newSet.add(element.id);
          // Also add ancestors
          const ancestors = getAncestorChain(element.id, elementMap);
          ancestors.forEach(ancestorId => newSet.add(ancestorId));
        }
      });
      
      return newSet;
    });
  }, [templates, elementTree, elementMap]);
  
  // Add trade-in vehicle quick action
  // Uses database alternate_context to determine which elements to auto-select
  // IMPORTANT: Does NOT add the vehicle parent element - this allows "trade-in only" mode
  // where tradeInEnabled is true but no buy vehicle is selected
  const addTradeInVehicle = useCallback(() => {
    if (tradeInEnabled) return;
    
    setTradeInEnabled(true);
    
    // Auto-select elements with trade-in alternate context
    if (!elementTree) return;
    
    // Get the vehicle element ID to exclude from ancestor selection
    const vehicleElement = slugMap.get('vehicle');
    const vehicleId = vehicleElement?.id;
    
    setSelectedElements(prev => {
      const newSet = new Set(prev);
      const allElements = flattenTree(elementTree);
      
      allElements
        .filter(el => hasTradeInAlternate(el.alternate_context))
        .forEach(element => {
          newSet.add(element.id);
          // Add ancestors EXCEPT the vehicle element itself
          // This allows trade-in-only mode where vehicle children are selected
          // but the vehicle parent is not (triggering trade-in-only rendering)
          const ancestors = getAncestorChain(element.id, elementMap);
          ancestors.forEach(ancestorId => {
            if (ancestorId !== vehicleId) {
              newSet.add(ancestorId);
            }
          });
        });
      
      return newSet;
    });
  }, [tradeInEnabled, elementTree, elementMap, slugMap]);
  
  // Remove trade-in vehicle
  const removeTradeIn = useCallback(() => {
    setTradeInEnabled(false);
  }, []);
  
  // Add co-buyer quick action
  // Uses database alternate_context to determine which elements to auto-select
  const addCoBuyer = useCallback(() => {
    if (coBuyerEnabled) return;
    
    setCoBuyerEnabled(true);
    
    // Auto-select elements with co-buyer alternate context
    if (!elementTree) return;
    
    setSelectedElements(prev => {
      const newSet = new Set(prev);
      const allElements = flattenTree(elementTree);
      
      allElements
        .filter(el => hasCoBuyerAlternate(el.alternate_context))
        .forEach(element => {
          newSet.add(element.id);
          // Also add ancestors
          const ancestors = getAncestorChain(element.id, elementMap);
          ancestors.forEach(ancestorId => newSet.add(ancestorId));
        });
      
      return newSet;
    });
  }, [coBuyerEnabled, elementTree, elementMap]);
  
  // Remove co-buyer
  const removeCoBuyer = useCallback(() => {
    setCoBuyerEnabled(false);
  }, []);
  
  // Check if vehicle element is selected
  const hasVehicleSelected = useMemo(() => {
    const vehicleElement = slugMap.get('vehicle');
    return vehicleElement ? selectedElements.has(vehicleElement.id) : false;
  }, [slugMap, selectedElements]);
  
  // Check if customer element is selected
  const hasCustomerSelected = useMemo(() => {
    const customerElement = slugMap.get('customer');
    return customerElement ? selectedElements.has(customerElement.id) : false;
  }, [slugMap, selectedElements]);
  
  // Generate XML from current selection
  const generateXml = useCallback((options: XmlGenerationOptions = {}): string => {
    if (!elementTree || elementTree.length === 0 || selectedElements.size === 0) {
      return '';
    }
    
    const opts: XmlGenerationOptions = {
      includeComments: false,
      indentSize: 2,
      includeOptionalAttributes: false,
      includeExampleValues: true,
      useMergeFields: false,
      ...options,
    };
    
    const lines: string[] = [];
    
    // Add proper XML declaration per ADF spec
    lines.push('<?xml version="1.0" encoding="UTF-8"?>');
    lines.push('<?adf version="1.0"?>');
    
    // Custom build to handle trade-in and co-buyer duplicates
    // Also ensures proper element ordering per ADF spec
    const buildProspectContent = (prospectElement: AdfElementNode): string => {
      const indentStr = '  '; // 2 spaces for prospect level content
      const innerLines: string[] = [];
      
      // Get selected children of prospect
      const selectedChildren = prospectElement.children.filter(child => selectedElements.has(child.id));
      
      // Check if vehicle is explicitly selected (for trade-in-only detection)
      const vehicleChild = prospectElement.children.find(c => c.tag_name === 'vehicle');
      const vehicleIsSelected = vehicleChild ? selectedElements.has(vehicleChild.id) : false;
      
      // Determine if this is a trade-in-only scenario:
      // tradeInEnabled is true but vehicle element itself is NOT selected
      // (vehicle children may be selected, which we'll use for trade-in)
      const isTradeInOnly = tradeInEnabled && !vehicleIsSelected;
      
      // Sort children using shared utility (id, requestdate, vehicle, customer, vendor, provider, leadtype)
      const orderedChildren = sortElementsByPriority(selectedChildren);
      
      // Handle trade-in-only: inject a trade-in vehicle before customers
      // when tradeInEnabled but vehicle element not selected
      if (isTradeInOnly && vehicleChild) {
        // Find insertion point (after leadtype/requestdate, before customer)
        const insertIndex = orderedChildren.findIndex(c => c.tag_name === 'customer');
        
        // Build trade-in-only vehicle using vehicle children that ARE selected
        if (opts.includeComments) {
          innerLines.push(`${indentStr}<!-- Trade-in vehicle -->`);
        }
        innerLines.push(buildXmlElement(vehicleChild, selectedElements, 1, opts, { vehicleInterest: 'trade-in' }));
        
        // Now render the rest (excluding vehicle since it's not selected)
        orderedChildren.forEach(child => {
          if (child.tag_name === 'customer' && coBuyerEnabled) {
            // First customer: primary buyer (with primarycontact="1")
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts));
            // Second customer: co-buyer (with primarycontact="0")
            innerLines.push('');
            if (opts.includeComments) {
              innerLines.push(`${indentStr}<!-- Co-buyer -->`);
            }
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts, { isCoBuyer: true }));
          } else {
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts));
          }
        });
      } else {
        // Normal flow: iterate through selected children
        orderedChildren.forEach(child => {
          // Special handling for vehicle with trade-in (buy + trade-in)
          if (child.tag_name === 'vehicle' && tradeInEnabled) {
            // First vehicle: interest="buy" with status="new"
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts, { vehicleInterest: 'buy' }));
            // Second vehicle: interest="trade-in" with status="used"
            innerLines.push('');
            if (opts.includeComments) {
              innerLines.push(`${indentStr}<!-- Trade-in vehicle -->`);
            }
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts, { vehicleInterest: 'trade-in' }));
          }
          // Single vehicle (no trade-in) - still pass 'buy' to get correct attributes
          else if (child.tag_name === 'vehicle') {
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts, { vehicleInterest: 'buy' }));
          }
          // Special handling for customer with co-buyer
          else if (child.tag_name === 'customer' && coBuyerEnabled) {
            // First customer: primary buyer (with primarycontact="1")
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts));
            // Second customer: co-buyer (with primarycontact="0")
            innerLines.push('');
            if (opts.includeComments) {
              innerLines.push(`${indentStr}<!-- Co-buyer -->`);
            }
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts, { isCoBuyer: true }));
          }
          else {
            innerLines.push(buildXmlElement(child, selectedElements, 1, opts));
          }
        });
      }
      
      return innerLines.join('\n');
    };
    
    // Find the adf root element
    const adfRoot = elementTree.find(el => el.tag_name === 'adf');
    if (!adfRoot || !selectedElements.has(adfRoot.id)) {
      return lines.join('\n');
    }
    
    // Build the adf element manually to handle special cases
    const attrStr = buildAttributeString(
      adfRoot.attributes as Record<string, AttributeConfig> | null,
      {
        includeOptional: opts.includeOptionalAttributes ?? true,
        useMergeFields: opts.useMergeFields ?? false,
        elementSlug: adfRoot.slug,
      }
    );
    const attrPart = formatAttributesForXml(attrStr);
    
    lines.push(`<adf${attrPart}>`);
    
    // Find prospect element
    const prospectElement = adfRoot.children.find(c => c.tag_name === 'prospect');
    if (prospectElement && selectedElements.has(prospectElement.id)) {
      const prospectAttrStr = buildAttributeString(
        prospectElement.attributes as Record<string, AttributeConfig> | null,
        {
          includeOptional: opts.includeOptionalAttributes ?? true,
          useMergeFields: opts.useMergeFields ?? false,
          elementSlug: prospectElement.slug,
        }
      );
      const prospectAttrPart = formatAttributesForXml(prospectAttrStr);
      
      if (opts.includeComments && prospectElement.description) {
        lines.push(`  <!-- ${prospectElement.description} -->`);
      }
      lines.push(`  <prospect${prospectAttrPart}>`);
      lines.push(buildProspectContent(prospectElement));
      lines.push('  </prospect>');
    }
    
    lines.push('</adf>');
    
    return lines.join('\n');
  }, [elementTree, selectedElements, tradeInEnabled, coBuyerEnabled]);
  
  // Get selection statistics
  const getSelectionStats = useCallback((): SelectionStats => {
    if (!elementTree) {
      return {
        totalSelected: 0,
        requiredCount: 0,
        optionalCount: 0,
        maxDepth: 0,
      };
    }
    
    let requiredCount = 0;
    let optionalCount = 0;
    
    selectedElements.forEach(id => {
      const element = elementMap.get(id);
      if (element) {
        if (element.is_required) {
          requiredCount++;
        } else {
          optionalCount++;
        }
      }
    });
    
    return {
      totalSelected: selectedElements.size,
      requiredCount,
      optionalCount,
      maxDepth: calculateMaxDepth(selectedElements, elementMap),
    };
  }, [elementTree, selectedElements, elementMap]);
  
  // Get selected elements as array (for iteration)
  const getSelectedElementsArray = useCallback((): AdfElementNode[] => {
    return Array.from(selectedElements)
      .map(id => elementMap.get(id))
      .filter((el): el is AdfElementNode => el !== undefined);
  }, [selectedElements, elementMap]);
  
  // Check if selection is valid (has required structure)
  const isValidSelection = useCallback((): boolean => {
    if (selectedElements.size === 0) return false;
    
    // Must have 'adf' root selected
    const adfRoot = Array.from(elementMap.values()).find(el => el.tag_name === 'adf');
    if (adfRoot && !selectedElements.has(adfRoot.id)) return false;
    
    // Check all required elements are selected
    const requiredIds = getRequiredElements();
    const hasAllRequired = requiredIds.every(id => selectedElements.has(id));
    
    return hasAllRequired;
  }, [selectedElements, elementMap, getRequiredElements]);
  
  return {
    // State
    selectedElements,
    isLoading,
    error,
    elementTree,
    elementMap,
    tradeInEnabled,
    coBuyerEnabled,
    hasVehicleSelected,
    hasCustomerSelected,
    templates, // Expose templates for UI
    
    // Actions
    toggleElement,
    selectBranch,
    deselectBranch,
    selectRequired,
    clearSelection,
    applyTemplate,
    addTradeInVehicle,
    removeTradeIn,
    addCoBuyer,
    removeCoBuyer,
    
    // Getters
    getRequiredElements,
    generateXml,
    getSelectionStats,
    getSelectedElementsArray,
    isValidSelection,
    
    // Helpers
    isSelected,
    isAnyChildSelected,
  };
}
